/**
 * Шаги «Визуалы» и «Карточки» пряников — API-клиент.
 *
 * Маршруты воронок серий и карточек пряников уже есть в гейтвее; здесь только вызовы и разбор ответов.
 * Права: запуск и создание — `ContentFunnel:UPDATE` (его проверяет гейтвей), чтение — `ContentFunnel:READ`.
 */

import { api } from '../../../shared/api/client'
import type { RunSummary, SeriesEstimate } from '../../content-funnel-series/types'
import { productsV2API } from '../../products-v2/api'
import { gingerbreadCatalogAPI } from '../api'
import type {
  ArticleMeta,
  CardBlocker,
  CardOutcome,
  CardOutcomeStatus,
  CardPlanPair,
  CardRejection,
  CardsPlan,
  CardsResult,
  SkuBatchExcludedTarget,
  SkuBatchItem,
  SkuBatchTarget,
  SlideSetMatch,
  VisualsProfile,
} from './types'

const SERIES = '/content-funnel-series'
const CARDS = '/gingerbread/cards'

/** Гейтвей отдаёт { success, data } — разворачиваем до data. */
const unwrap = (response: any) => response?.data?.data ?? response?.data

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])
const text = (value: unknown): string | null => (value == null || value === '' ? null : String(value))
const numberOrNull = (value: unknown): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function toExcluded(raw: unknown): SkuBatchExcludedTarget {
  const row = asRecord(raw)
  return {
    marketplace: String(row.marketplace ?? ''),
    cabinet: text(row.cabinet),
    code: String(row.code ?? ''),
    message: String(row.message ?? ''),
  }
}

function toTarget(raw: unknown): SkuBatchTarget {
  const row = asRecord(raw)
  return { marketplace: String(row.marketplace ?? ''), cabinet: String(row.cabinet ?? '') }
}

export function toSkuBatchItems(raw: unknown): SkuBatchItem[] {
  return asArray(raw).map((entry): SkuBatchItem => {
    const row = asRecord(entry)
    const error = row.error ? asRecord(row.error) : null
    return {
      article: String(row.article ?? ''),
      runId: text(row.runId),
      slideSetIds: asArray(row.slideSetIds).map(Number).filter((id) => Number.isInteger(id)),
      targets: asArray(row.targets).map(toTarget),
      excludedTargets: asArray(row.excludedTargets).map(toExcluded),
      error: error ? { code: String(error.code ?? ''), message: String(error.message ?? 'Ошибка') } : null,
    }
  })
}

const toBlockers = (raw: unknown): CardBlocker[] =>
  asArray(raw).map((entry) => {
    const row = asRecord(entry)
    return { code: String(row.code ?? ''), message: String(row.message ?? '') }
  })

const toRejections = (raw: unknown): CardRejection[] =>
  asArray(raw).map((entry) => {
    const row = asRecord(entry)
    return { subject: String(row.subject ?? ''), code: String(row.code ?? ''), message: String(row.message ?? '') }
  })

function toPair(raw: unknown): CardPlanPair {
  const row = asRecord(raw)
  return {
    article: String(row.article ?? ''),
    marketplace: String(row.marketplace ?? ''),
    sellerId: Number(row.sellerId ?? 0),
    sellerName: text(row.sellerName),
    cabinet: String(row.cabinet ?? ''),
    ready: row.ready === true,
    blockers: toBlockers(row.blockers),
    photos: numberOrNull(row.photos) ?? 0,
    cardId: numberOrNull(row.cardId),
    cardStatus: text(row.cardStatus),
  }
}

const OUTCOME_STATUSES: CardOutcomeStatus[] = ['created', 'skipped', 'blocked', 'failed']

function toOutcome(raw: unknown): CardOutcome {
  const row = asRecord(raw)
  const status = OUTCOME_STATUSES.find((candidate) => candidate === row.status) ?? 'failed'
  return {
    article: String(row.article ?? ''),
    marketplace: String(row.marketplace ?? ''),
    sellerId: Number(row.sellerId ?? 0),
    sellerName: text(row.sellerName),
    cabinet: String(row.cabinet ?? ''),
    status,
    cardId: numberOrNull(row.cardId),
    cardStatus: text(row.cardStatus),
    blockers: toBlockers(row.blockers),
  }
}

/** Тело плана и создания карточек: профиль пряников сервер подставляет сам, поэтому уходит только `layout`. */
function cardsBody(articles: string[], sellerIds: number[], profile: VisualsProfile) {
  const body: Record<string, unknown> = { articles, targets: sellerIds.map((sellerId) => ({ sellerId })) }
  if (profile === 'layout') body.profile = profile
  return body
}

export const gingerbreadVisualsFlowAPI = {
  /**
   * Пакет SKU-запусков: по запуску на артикул (× группу наборов). Профиль по умолчанию — пряники; наборы подбираются
   * по категории и кабинету среди наборов этого профиля.
   */
  async createSkuRuns(articles: string[], targets: SkuBatchTarget[], profile: VisualsProfile = 'gingerbread'): Promise<SkuBatchItem[]> {
    const response = await api.post(`${SERIES}/runs/sku-batch`, { articles, targets, profile })
    return toSkuBatchItems(unwrap(response))
  },

  /** SKU-запуски профиля (по умолчанию пряники): все либо одного артикула (`article=`). Сервер отдаёт до 200 последних. */
  async listSkuRuns(article?: string, signal?: AbortSignal, profile: VisualsProfile = 'gingerbread'): Promise<RunSummary[]> {
    const params: Record<string, string> = { scope: 'sku', profile }
    if (article) params.article = article
    const rows = unwrap(await api.get(`${SERIES}/runs`, { params, signal }))
    return Array.isArray(rows) ? rows : []
  },

  /** Смета по наборам слайдов (один запуск). */
  async estimate(slideSetIds: number[], signal?: AbortSignal): Promise<SeriesEstimate> {
    const params = { slideSetIds: [...slideSetIds].sort((a, b) => a - b).join(',') }
    return unwrap(await api.get(`${SERIES}/estimate`, { params, signal })) as SeriesEstimate
  },

  /**
   * Какие наборы закрывают какие кабинеты для категории. `profile` оставляет только наборы этого профиля
   * (макету не подбираются пряничные наборы и наоборот); без него — наборы любых профилей. `designType` — тип макета
   * артикула (наборы, привязанные к типу, как «Макеты · Круг»).
   */
  async matchSlideSets(
    categoryId: number,
    targets: SkuBatchTarget[],
    signal?: AbortSignal,
    profile?: VisualsProfile,
    designType?: string | null,
  ): Promise<SlideSetMatch> {
    const params: Record<string, string> = {
      categoryIds: String(categoryId),
      targets: targets.map((target) => `${target.marketplace}:${target.cabinet}`).join(','),
    }
    if (profile) params.profile = profile
    // Тип макета: набор «Макеты · Круг» входит в план только при типе; без него подберутся наборы без привязки к типу.
    if (designType) params.designType = designType
    const data = asRecord(unwrap(await api.get(`${SERIES}/slide-sets/match`, { params, signal })))
    return {
      targets: asArray(data.targets).map((entry) => {
        const row = asRecord(entry)
        return {
          marketplace: String(row.marketplace ?? ''),
          cabinet: text(row.cabinet),
          sets: asArray(row.sets).map((set) => {
            const item = asRecord(set)
            return { id: Number(item.id), name: String(item.name ?? '') }
          }),
        }
      }),
    }
  },

  /**
   * Название и категория товаров по артикулам (не больше 100 за вызов): пряники — из каталога пряников,
   * макеты — из каталога товаров.
   */
  async loadArticleMeta(articles: string[], signal?: AbortSignal, profile: VisualsProfile = 'gingerbread'): Promise<ArticleMeta[]> {
    if (articles.length === 0) return []
    if (profile === 'layout') {
      const catalog = await productsV2API.getProducts({ articles: articles.join(','), limit: articles.length, offset: 0 }, signal)
      return catalog.products.map((row: any) => {
        const designType = text(row.design?.type ?? row.design?.design_type)
        return {
          article: String(row.article ?? ''),
          title: text(row.title),
          categoryId: numberOrNull(row.category_id ?? row.category?.category_id),
          ...(designType ? { designType } : {}),
        }
      })
    }
    const result = await gingerbreadCatalogAPI.getCatalog(
      { articles: articles.join(','), limit: articles.length, offset: 0 },
      signal,
    )
    return result.products.map((row: any) => ({
      article: String(row.article ?? ''),
      title: text(row.title),
      categoryId: numberOrNull(row.category_id ?? row.category?.category_id),
    }))
  },

  /**
   * Предпроверка пар «артикул × кабинет»: до 100 артикулов и 30 кабинетов за вызов. Профиль `layout` — макеты
   * (те же маршруты, в теле `profile`); для пряников тело прежнее, без профиля.
   */
  async cardsPlan(articles: string[], sellerIds: number[], profile: VisualsProfile = 'gingerbread'): Promise<CardsPlan> {
    const response = await api.post(`${CARDS}/plan`, cardsBody(articles, sellerIds, profile))
    const data = asRecord(unwrap(response))
    return {
      pairs: asArray(data.pairs).map(toPair),
      rejectedArticles: toRejections(data.rejectedArticles),
      rejectedTargets: toRejections(data.rejectedTargets),
    }
  },

  /** Создание черновиков по готовым парам (остальные сервер вернёт блокировками). Профиль — как у `cardsPlan`. */
  async createCards(articles: string[], sellerIds: number[], profile: VisualsProfile = 'gingerbread'): Promise<CardsResult> {
    const response = await api.post(CARDS, cardsBody(articles, sellerIds, profile))
    const data = asRecord(unwrap(response))
    return {
      pairs: asArray(data.pairs).map(toOutcome),
      rejectedArticles: toRejections(data.rejectedArticles),
      rejectedTargets: toRejections(data.rejectedTargets),
    }
  },
}
